<template>
  <div class="users-list">
    <template v-if="isLoadedFilters">
      <div class="users-list__filters app-list-filters">
        <a-row
          :gutter="filterGutter"
          type="flex"
        >
          <a-col span="8">
            <template v-if="isFilterAllOver">
              <custom-input
                v-model="search"
              />
            </template>
            <template v-else-if="isFilterByEth">
              <custom-input
                v-model="search"
              />
            </template>
            <template v-else>
              <custom-input
                v-model="search"
              />
            </template>
          </a-col>
          <a-col span="14">
            <m-select
              :options="usersRoles"
              :value="filters.role"
              :disabled="isPending"
              :on-change-values="onChangeUserRoles"
            />
          </a-col>
          <a-col
            span="1"
            v-if="isAdmin()">
            <export-users
              :filters="filters"
              :loading="loadingExport"
              :on-get-download-url="getExportUsersUrl"
              :limit="usersList.length">
              {{ "export.label" | globalize }}
            </export-users>
          </a-col>
        </a-row>
      </div>
      <div class="users-list__list-wrp">
        <div class="app-list">
          <div class="app-list__header">
            <span class="app-list__cell app-list__cell--left">
              {{ "users-list.title-user-id" | globalize }}
            </span>
            <span class="app-list__cell app-list__cell--center">
              {{ "users-list.title-user-role" | globalize }}
            </span>
            <span class="app-list__cell app-list__cell--right">
              {{ "users-list.title-email" | globalize }}
            </span>
            <span class="app-list__cell app-list__cell--right">
              Source Key
            </span>
          </div>
          <template v-if="isLoaded && usersList.length && !isFailed">
            <button
              v-ripple
              :disabled="userDetailsDisabled"
              class="app-list__li"
              v-for="item in usersList"
              :key="`user-${item.id}`"
              @click="showUserDetails(item.id)"
            >
              <span
                class="app-list__cell app-list__cell--left"
                :title="item.id"
              >
                <copyable :copy-text="item.id.toString()">
                  {{ item.id }}
                </copyable>
              </span>

              <span
                class="app-list__cell app-list__cell--center"
                :title="item.role"
              >
                {{ item.role | formatUserRole }}
              </span>

              <span
                class="app-list__cell app-list__cell--right"
                :title="item.email"
              >
                <copyable :copy-text="item.email">{{ item.email }}</copyable>
              </span>

              <span
                class="app-list__cell app-list__cell--right"
                :title="item.source_key"
              >
                {{ item.source_key || '—' }}
              </span>
            </button>
          </template>

          <template v-else>
            <div class="app-list__li-like">
              <template v-if="isLoaded && !usersList.length">
                <p>{{ "users-list.nothing-here-yet" | globalize }}</p>
              </template>
              <template v-else-if="isFailed">
                <p>{{ "users-list.default-error-message" | globalize }}</p>
              </template>
              <template v-else>
                <p>{{ "users-list.loading-message" | globalize }}</p>
              </template>
            </div>
          </template>
        </div>

        <div class="app__more-btn-wrp">
          <collection-loader
            class="app__more-btn-wrp"
            :first-page-loader="getUsers"
            @first-page-load="setList"
            @next-page-load="concatList"
            ref="collectionLoaderBtn"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <loader
        message-id="loading"
        position-center
      />
    </template>
  </div>
</template>

<script>
import CustomInput from '@/vue/fields/CustomInput'
import PermissionMixin from '@/vue/mixins/permission.mixin'
import Loader from '@/vue/common/Loader'
import ARow from 'ant-design-vue/lib/row'
import ACol from 'ant-design-vue/lib/col'
import { mapGetters } from 'vuex'
import CollectionLoader from '@/vue/common/CollectionLoader'
import Copyable from '@/vue/common/copyable/Copyable'
import FormMixin from '@/vue/mixins/form.mixin'
import ExportUsers from '@/vue/common/export'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { UserRecord } from '@/js/records/entities/user.record'
import { vueRoutes } from '@/vue-router/routes'
import { vuexTypes } from '@/vuex'
import { clearObject } from '@/js/helpers/clear-object'
import { addParamsToLocation, queryToFilters } from '@/js/helpers/router-query'
import { api } from '@/api'
import MSelect from '@/vue/fields/MSelect'

const defaultFilters = {
  id: '',
  role: [],
  email: '',
  eth_address: '',
  name: '',
}

export default {
  name: 'users-list',
  components: {
    Loader,
    'custom-input': CustomInput,
    CollectionLoader,
    'copyable': Copyable,
    ExportUsers,
    MSelect,
    ARow,
    ACol,
  },
  mixins: [ FormMixin, PermissionMixin ],
  data () {
    return {
      search: '',
      loadingExport: false,
      isLoaded: false,
      isLoadedFilters: false,
      isPending: false,
      isFailed: false,
      usersList: [],
      isFilterAllOver: true,
      isFilterByEth: false,
      filters: defaultFilters,
      exportUrl: '',
      filterGutter: { xs: 1, sm: 1, md: 2, lg: 2 },
    }
  },
  computed: {
    ...mapGetters([vuexTypes.account]),
    ...mapGetters([vuexTypes.accountAddress]),
    currentUser () {
      return {
        role: this.account.role,
        id: this.account.id,
        ethAddress: this.accountAddress,
      }
    },
    userDetailsDisabled () {
      const result = this.isForbidden('users', 'user', 'read')
      return result
    },
  },
  watch: {
    filters: {
      handler () {
        this.isPending = true
        if (this.isLoaded) {
          this.reloadList()
        }
      },
      deep: true,
    },

    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },

    search: {
      handler () {
        this.chooseFilter()
      },
    },
    '$route' () {
      this.filters = queryToFilters(defaultFilters)
      this.search = this.oneOf(this.filters)
      return this.getUsers()
    },
  },

  async created () {
    await this.getUsersRoles()
    this.filters = queryToFilters(defaultFilters)
    this.search = this.oneOf(this.filters)
    await this.getUsers()
  },

  methods: {
    isAdmin () {
      return this.currentUser.role === 'admin'
    },
    async getUsersRoles () {
      this.isLoadedFilters = false
      try {
        const { data } = await api.getWithSignature('/roles')
        this.usersRoles = data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
      this.isLoadedFilters = true
    },

    async getUsers () {
      this.isLoaded = false
      this.isFailed = false
      let response = {}

      try {
        const roles = this.filters.role.join(',')
        const query = {
          filter: clearObject({
            role: roles,
            user_id: this.filters.id,
            email_search: this.filters.email,
            source_key: this.currentUser.role === 'rialto-admin' ? 'rialto' : undefined,
            address_search: this.filters.eth_address,
            name_search: this.filters.name,
          }),
        }
        response = await api.getWithSignature('/users', query)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      }

      this.isLoaded = true
      return response
    },

    async getUserData (id) {
      try {
        let { data } = await api.getWithSignature(`/userdata/${id}`)
        return typeof data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getVerification (id) {
      try {
        const query = {
          filter: clearObject({
            user_id: id,
          }),
        }
        let { data } = await api.getWithSignature('/verifications', query)
        return data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    setList (data) {
      this.usersList = data.map(i => new UserRecord(i))
      for (let ind in this.usersList) {
        if (this.usersList[ind].role === 'tier99') {
          this.addEmailtToRejectedUsers(this.usersList[ind])
        }
      }
    },

    async addEmailtToRejectedUsers (user) {
      let userdata = await this.getUserData(user.id)
      if (userdata === undefined) {
        let verification = await this.getVerification(user.id)
        user.email = verification[0].data.pd.email
      }
    },

    concatList (data) {
      this.usersList = this.usersList.concat(data.map(i => new UserRecord(i)))
      for (let ind in this.usersList) {
        if (this.usersList[ind].role === 'tier99') {
          this.addEmailtToRejectedUsers(this.usersList[ind])
        }
      }
    },

    reloadList () {
      addParamsToLocation(this.$route, this.filters)
      this.$refs.collectionLoaderBtn.loadFirstPage()
    },

    setFilterAllOver () {
      this.isFilterAllOver = true
      this.isFilterByEth = false
    },

    setFilterByEth () {
      this.isFilterAllOver = false
      this.isFilterByEth = true
    },

    setFilterByEmail () {
      this.isFilterAllOver = false
      this.isFilterByEth = false
    },

    clearFilters (opts = {}) {
      Object.getOwnPropertyNames(this.filters).forEach(prop => {
        let excluded = false
        if (opts.exclude && Array.isArray(opts.exclude)) {
          excluded = opts.exclude.find(r => r === prop)
        }
        if (excluded) {
          return
        }

        this.filters[prop] = ''
      })
    },

    showUserDetails (id) {
      this.$router.push({
        name: vueRoutes.userDetails.name,
        params: { id },
      })
    },

    chooseFilter () {
      this.clearFilters(
        {
          exclude: [
            'role',
          ],
        }
      )
      if (this.search.includes('@')) {
        this.filters.email = this.search.trim()
        this.setFilterByEmail()
      } else if (this.search.startsWith('0x')) {
        this.filters.eth_address = this.search.trim()
        this.setFilterByEth()
      } else if (this.search.match(/^[0-9]+$/) != null && this.search[0] !== '0') {
        this.filters.id = this.search.trim()
        this.setFilterAllOver()
      } else if (this.search === '') {
        this.clearFilters(
          {
            exclude: [
              'role',
            ],
          }
        )
      } else {
        this.filters.name = this.search.trim()
      }
    },

    async getExportUsersUrl (query) {
      this.loadingExport = true
      this.isFailed = false
      let response = {}

      try {
        response = await api.getWithSignature('/export/users/csv', { ...query })
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      }
      this.loadingExport = false
      return response.data.downloadUrl.replace('http://', 'https://')
    },

    onChangeUserRoles (value) {
      this.filters.role = value
    },

    oneOf (filters) {
      let result = ''

      Object.keys(filters).forEach(k => {
        if (k === 'role') return
        if (filters[k]) {
          result = filters[k]
        }
      })
      return result
    },

  },
}
</script>

<style lang="scss" scoped>
@import '~ant-design-vue/lib/grid/style/index.css';

.users-list__list-wrp {
  margin-top: 4rem;
}

.users-list__btn-filters {
  max-width: 20rem;
  width: 100%;
}

.app-list-filters {
  display: inherit;
}

</style>
